import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export type ErrorSource = 'web' | 'api';
export type ErrorWindow = '24h' | '7d' | '30d';
export type ErrorState = 'open' | 'resolved' | 'all';

/** One grouped error (ops.client_errors row) — see apps/api/src/modules/client-errors. */
export interface AdminErrorGroup {
  id: string;
  source: ErrorSource;
  message: string;
  topFrame: string | null;
  sampleStack: string | null;
  sampleRoute: string | null;
  sampleRelease: string | null;
  sampleUserAgent: string | null;
  sampleUserId: string | null;
  sampleStatus: number | null;
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

export interface ListErrorsQuery {
  source?: ErrorSource;
  window: ErrorWindow;
  state: ErrorState;
  limit: number;
  offset: number;
}

export interface ListErrorsResponse {
  total: number;
  summary: { web: number; api: number };
  items: AdminErrorGroup[];
}

const ERRORS_KEY: QueryKey = ['admin', 'errors'];

function buildQS(q: ListErrorsQuery): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  return `?${sp.toString()}`;
}

export function useAdminErrors(query: ListErrorsQuery) {
  return useQuery({
    queryKey: [...ERRORS_KEY, query],
    queryFn: () => api<ListErrorsResponse>(`/api/admin/errors${buildQS(query)}`),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });
}

export function useResolveError() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
      api<AdminErrorGroup>(`/api/admin/errors/${id}/resolve`, { method: 'POST', body: { resolved } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ERRORS_KEY }),
  });
}
